import type { ComponentPropsWithoutRef, FunctionComponent } from 'react'
import React from 'react'

import classnames from 'classnames'

import type { ValidTitleTags } from '../../../types'
import { Hr } from '../../Hr'
import { Link } from '../../Link'
import { TypographyV2 } from '../../Seatbelt2/Foundations/Typography'
import styles from './MultiLinkBlock.module.scss'
import { linkComponentMapper } from './MultiLinkBlock.utils'

export const MultiLinkBlock: FunctionComponent<Props> = ({
  className,
  componentType,
  title,
  titleLink = '',
  viewAllLink,
  headerTag = 'h2',
  links,
  isOrdered = false,
  titleLinkProps,
  ...rest
}) => {
  if (!links?.length) {
    return null
  }

  const ListElement = isOrdered ? 'ol' : 'ul'

  return (
    <section className={className} {...rest} data-testid="multi-link-wrapper">
      {titleLink ? (
        <Link
          href={titleLink}
          textStyle="sb-text-heading-medium"
          includeIcon={true}
          appearance="secondary"
          className={styles.multiLinkTitleLink}
          {...titleLinkProps}
        >
          {title}
        </Link>
      ) : (
        <TypographyV2
          tag={headerTag}
          textStyle="sb-text-heading-small"
          className="sb-padding-bottom-3"
          data-testid="multi-link-title"
        >
          {title}
        </TypographyV2>
      )}
      <nav aria-label={`${title} Links`}>
        {links && (
          <ListElement
            data-testid="list-element"
            className={
              !isOrdered ? styles.multiLinkList : styles.multiLinkListOrdered
            }
          >
            {getLinks(links, componentType, isOrdered)}
          </ListElement>
        )}
        {viewAllLink && (
          <>
            <Hr
              className="sb-margin-vertical-3 sb-bg-color-black-20"
              fullWidth={true}
            />
            <Link {...viewAllLink} className={styles.multiLinkFooterCta}>
              {viewAllLink.text}
            </Link>
          </>
        )}
      </nav>
    </section>
  )
}

///////// IMPLEMENTATION /////////

const getLinks = (links: Links, componentType?: string, isOrdered = false) => {
  if (!links?.length) {
    return null
  }

  return links.map((props, index) => {
    const { type, text, ...restProps } = props
    const linkType = type || 'standard'
    const { Component, ...linkProps } = linkComponentMapper[linkType]

    return (
      text && (
        <li
          key={`${text}-${index}`}
          className={classnames(styles.multiLinkListItem)}
        >
          <Component
            linkStyle={isOrdered ? 'primary' : 'secondary'}
            data-which-id={
              componentType && `${componentType}-link-${index + 1}`
            }
            {...restProps}
            {...(isOrdered && { includeIcon: false })}
            {...linkProps}
          >
            {text}
          </Component>
        </li>
      )
    )
  })
}

///////// IMPLEMENTATION /////////

type Props = {
  className?: string
  componentType?: string
  title: string
  titleLink?: string
  titleLinkProps?: Parameters<typeof Link>[0]
  headerTag?: ValidTitleTags
  viewAllLink?: ViewAllLink | null
  links?: Links
  isOrdered?: boolean
  maxNumLinks?: {
    standard?: number
    product?: number
  }
} & ComponentPropsWithoutRef<'section'>

export type ViewAllLink = {
  text: string
} & Parameters<typeof Link>[0]

export type Links = ({
  text: string
  type?: 'bestBuy' | 'dontBuy' | 'standard'
} & Parameters<typeof Link>[0])[]
